import type { ReactNode } from "react";

interface GoalRingProps {
  /** Avance de la meta, de 0 a 100 (se clampa). */
  percent: number;
  /** Lado del anillo en px. */
  size: number;
  /** Grosor del trazo en px. */
  stroke: number;
  /** Color CSS del arco de progreso, p. ej. "var(--ds-card-lilac)". */
  color: string;
  /** Color CSS de la pista, p. ej. "var(--ds-track-lilac)". */
  track: string;
  /** Texto accesible del anillo (aria-label). */
  label: string;
  /** Contenido centrado sobre el anillo (porcentaje, rótulo). */
  children?: ReactNode;
}

/** Anillo de avance de una meta. Arranca a las 12 en punto y tiene extremos redondeados. */
export const GoalRing = ({ percent, size, stroke, color, track, label, children }: GoalRingProps) => {
  const clamped = Number.isFinite(percent) ? Math.min(100, Math.max(0, percent)) : 0;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;
  const arcLength = (circumference * clamped) / 100;

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={label} className="block">
        <circle cx={center} cy={center} r={radius} fill="none" strokeWidth={stroke} style={{ stroke: track }} />
        {clamped > 0 && (
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${arcLength.toFixed(1)} ${circumference.toFixed(1)}`}
            transform={`rotate(-90 ${center} ${center})`}
            style={{ stroke: color }}
          />
        )}
      </svg>
      {children && <div className="absolute inset-0 flex flex-col items-center justify-center">{children}</div>}
    </div>
  );
};
